import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, SquareCheckBig, PhoneCall, Settings2, CalendarDays, Loader2, Phone, ChevronDown, BookOpen, Eye, Bot, Search, X, ListTodo } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useControlCentreGate } from '../hooks/useControlCentreGate';
import { useBeliefGraph } from '../hooks/useBeliefGraph';
import { useExtractionStatus } from '../hooks/useExtractionStatus';
import { useTasks } from '../hooks/useTasks';
import { useDayFocus } from '../hooks/useDayFocus';
import { CollapsibleTaskCard } from '../components/tasks/TaskCard';
import { QuickTaskIcon } from '../components/tasks/QuickTaskIcon';
import { QuickPlanModal } from '../components/tasks/QuickPlanModal';
import { callGenerateQuickPlan, callInitiatePhoneCall } from '../services/functions';
import { getSessionProcessingCopy, getSessionProcessingLine } from '../utils/sessionProcessingCopy';
import { brandColors } from '@/design';
import type { ActionItemStatus, DayFocus, DayFocusPriority, SessionType, TaskPlan } from '../types';
import type { GenerateQuickPlanResponse } from '../services/functions';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profile } = useProfile(user?.uid ?? null);
  const { gate: controlCentreGate } = useControlCentreGate(user?.uid ?? null, profile);
  const { nodes, loading: graphLoading } = useBeliefGraph(user?.uid ?? null);
  const { isExtracting, extractingSessionType, extractingSessionId } = useExtractionStatus(user?.uid ?? null);
  const { activeTasks, toggle, addItem, deleteItem, reorder } = useTasks(user?.uid ?? null);
  const {
    allFocuses,
    primaryFocus,
    createFocus,
    toggle: toggleFocusItem,
    addItem: addFocusItem,
    deleteItem: deleteFocusItem,
    reorder: reorderFocusItems,
  } = useDayFocus(user?.uid ?? null);

  const hour = new Date().getHours();
  const greeting =
    hour >= 5 && hour < 12 ? 'Good morning'
    : hour >= 12 && hour < 17 ? 'Good afternoon'
    : 'Good evening';
  const fullName = profile?.displayName ?? user?.displayName ?? '';
  const displayName = profile?.preferredName || fullName.split(' ')[0] || fullName;

  const isOnboarded = profile?.onboardingComplete || nodes.length > 0;

  const today = new Date();
  const dayNum = today.getDate();
  const monthStr = MONTHS[today.getMonth()];

  const defaultMode = profile?.preferences?.preferredModality ?? 'voice';
  const phoneCallsEnabled = Boolean(profile?.preferences?.phoneCallsEnabled);
  const phoneNumber = profile?.preferences?.phoneNumber?.trim() ?? '';
  const phoneNumberVerified = Boolean(profile?.preferences?.phoneNumberVerified);
  const phoneCallSetupState =
    phoneNumber.length === 0
      ? {
          message: 'Add your mobile number in Settings to start sessions by phone.',
          ctaLabel: 'Add your number here',
        }
      : !phoneNumberVerified
        ? {
            message: 'Verify your mobile number in Settings to start sessions by phone.',
            ctaLabel: 'Verify your number here',
          }
        : !phoneCallsEnabled
          ? {
              message: 'Enable phone calls in Settings to start sessions by phone.',
              ctaLabel: 'Enable phone calls here',
            }
          : null;
  const canPlacePhoneCalls = phoneCallSetupState === null;

  const [pendingPhoneSessionType, setPendingPhoneSessionType] = useState<SessionType | null>(null);
  const [isPlacingCall, setIsPlacingCall] = useState(false);
  const [phoneCallStatus, setPhoneCallStatus] = useState<string | null>(null);
  const [phoneCallError, setPhoneCallError] = useState<string | null>(null);
  const processingCopy = getSessionProcessingCopy(extractingSessionType);
  const processingLine = getSessionProcessingLine(extractingSessionType, extractingSessionId);

  function startSession(type: SessionType) {
    navigate(`/session?type=${type}&mode=${defaultMode}`);
  }

  function requestPhoneCall(type: SessionType) {
    setPhoneCallStatus(null);
    setPhoneCallError(null);
    setPendingPhoneSessionType(type);
  }

  async function confirmPhoneCall() {
    if (!pendingPhoneSessionType || isPlacingCall) return;
    setIsPlacingCall(true);
    setPhoneCallError(null);
    try {
      await callInitiatePhoneCall(pendingPhoneSessionType);
      const phone = profile?.preferences?.phoneNumber ?? 'your number';
      setPhoneCallStatus(`Calling now — ${phone}`);
      setPendingPhoneSessionType(null);
    } catch (error) {
      const message =
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not place a call right now.';
      setPhoneCallError(message);
    } finally {
      setIsPlacingCall(false);
    }
  }

  const createQuickPlan = useCallback(
    async (input: { instruction: string; inputMode: 'text' | 'voice' }): Promise<GenerateQuickPlanResponse> => (
      callGenerateQuickPlan(input.instruction, input.inputMode)
    ),
    []
  );

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <div
        className="sticky top-0 z-40"
        style={{
          background: 'var(--color-background)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <header className="px-5 pt-4 pb-2 lg:px-8 max-w-lg lg:max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/Flank Logo.svg" alt="Flank" className="h-9 w-auto" />
            <div className="flex flex-col justify-center gap-0.5">
              <p className="text-[11px] leading-tight text-[var(--color-text-secondary)]">{greeting}</p>
              <h1 className="text-xl leading-tight font-bold text-[var(--color-text-primary)]">
                {displayName || 'Welcome'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/tasks/today')}
              className="flex flex-col items-center justify-center w-10 h-10 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              title="Today's tasks"
            >
              <span className="text-sm font-bold leading-none">{dayNum}</span>
              <span className="text-[9px] uppercase leading-none mt-0.5 opacity-70">{monthStr}</span>
            </button>
            <button
              onClick={() => navigate('/tasks/todos')}
              className="p-2.5 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              title="Todos — lanes & Kanban"
            >
              <ListTodo size={20} />
            </button>
            <button
              onClick={() => navigate('/journal')}
              className="p-2.5 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              title="Journal"
            >
              <BookOpen size={20} />
            </button>
            <button
              onClick={() => navigate('/tasks/timeline')}
              className="p-2.5 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              title="Timeline"
            >
              <CalendarDays size={20} />
            </button>
            <button
              onClick={() => navigate('/control')}
              className="p-2.5 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              title="Agent Control Centre"
              style={{ display: controlCentreGate.available ? 'inline-flex' : 'none' }}
            >
              <Bot size={20} />
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="p-2.5 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              title="Settings"
            >
              <Settings2 size={20} />
            </button>
          </div>
        </header>
      </div>

      <div className="pt-4 pb-10 px-5 lg:px-8 max-w-lg mx-auto lg:max-w-5xl">
        <div className="flex flex-col gap-5">

          {!graphLoading && !isOnboarded && (
            <div
              className="rounded-2xl p-6 flex flex-col gap-4"
              style={{
                background: `linear-gradient(135deg, ${brandColors.teal}22, ${brandColors.lightBlue}22)`,
                border: `1px solid ${brandColors.teal}44`,
              }}
            >
              <div>
                <h2 className="font-semibold text-[var(--color-text-primary)]">
                  Let's get to know you
                </h2>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  A 10-minute conversation to get to know you and make every conversation richer.
                </p>
              </div>
              <button
                onClick={() => startSession('onboarding')}
                className="flank-btn flank-btn-primary self-start"
              >
                Start onboarding
              </button>
            </div>
          )}

          {/* Session cards: 2×2 on mobile, 4-in-a-row on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SessionCard
              title="Morning Check-In"
              description="Set your intention and explore how you're feeling"
              icon={<Sun size={22} />}
              sessionType="morning_checkin"
              onStart={startSession}
              accentColor={brandColors.teal}
            />
            <SessionCard
              title="Task Planning"
              description="Think through a task and build an action plan"
              icon={<SquareCheckBig size={22} />}
              sessionType="task_planning"
              onStart={startSession}
              accentColor={brandColors.darkBlue}
              iconColor="#ffffff"
            />
            <SessionCard
              title="Coaching Call"
              description="Talk through challenges with your advisor"
              icon={<PhoneCall size={22} />}
              sessionType="coaching"
              onStart={startSession}
              accentColor="#8B5CF6"
            />
            <SessionCard
              title="Evening Check-In"
              description="Reflect on the day and notice what shifted"
              icon={<Moon size={22} />}
              sessionType="evening_checkin"
              onStart={startSession}
              accentColor={brandColors.lightBlue}
            />
          </div>

          <CallMeSection
            canPlaceCalls={canPlacePhoneCalls}
            setupMessage={phoneCallSetupState?.message ?? null}
            setupCtaLabel={phoneCallSetupState?.ctaLabel ?? null}
            onSelectSession={requestPhoneCall}
            onOpenPhoneSetup={() => navigate('/settings?tab=phone')}
          />

          <FocusedTasks
            dayFocuses={allFocuses}
            hasPrimaryDayFocus={Boolean(primaryFocus)}
            onCreateDayFocus={createFocus}
            onCreateQuickPlan={createQuickPlan}
            onToggleFocusItem={toggleFocusItem}
            onAddFocusItem={addFocusItem}
            onDeleteFocusItem={deleteFocusItem}
            onReorderFocusItems={reorderFocusItems}
            tasks={activeTasks}
            onToggle={toggle}
            onAddItem={addItem}
            onDeleteItem={deleteItem}
            onReorder={reorder}
            onViewAll={() => navigate('/tasks/today?showAll=true')}
          />

          {isExtracting && (
            <div
              className="rounded-2xl px-5 py-4 flex items-center gap-3 flank-appear"
              style={{
                background: `linear-gradient(135deg, ${brandColors.teal}15, ${brandColors.lightBlue}15)`,
                border: `1px solid ${brandColors.teal}33`,
              }}
            >
              <Loader2
                size={18}
                className="animate-spin shrink-0"
                style={{ color: brandColors.teal }}
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  {processingCopy.title}
                </p>
                <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                  {processingLine}
                </p>
              </div>
            </div>
          )}

          {phoneCallStatus && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{ background: 'var(--color-info-subtle)', color: 'var(--color-info)' }}
            >
              {phoneCallStatus}
            </div>
          )}

          {phoneCallError && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{ background: 'var(--color-error-subtle)', color: 'var(--color-error)' }}
            >
              {phoneCallError}
            </div>
          )}

        </div>
      </div>

      {pendingPhoneSessionType && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-sm rounded-2xl p-6 flex flex-col gap-4"
            style={{ background: 'var(--color-background)' }}
          >
            <div>
              <h3 className="font-semibold text-[var(--color-text-primary)]">Call me now?</h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                We&apos;ll place a phone call to your verified number and start this session by voice.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setPendingPhoneSessionType(null); setPhoneCallError(null); }}
                className="flank-btn flank-btn-secondary flex-1"
                disabled={isPlacingCall}
              >
                Cancel
              </button>
              <button
                onClick={confirmPhoneCall}
                className="flank-btn flank-btn-primary flex-1"
                disabled={isPlacingCall}
              >
                {isPlacingCall ? 'Calling...' : 'Place call'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Session Card (2×2 grid) ──────────────────────────────────────────────────

function SessionCard({
  title,
  description,
  icon,
  sessionType,
  onStart,
  accentColor,
  iconColor,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  sessionType: SessionType;
  onStart: (type: SessionType) => void;
  accentColor: string;
  iconColor?: string;
}) {
  return (
    <button
      onClick={() => onStart(sessionType)}
      className="flank-card rounded-2xl p-4 flex flex-col items-start gap-3 flank-appear text-left transition-all hover:scale-[1.01] cursor-pointer w-full"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${accentColor}22`, color: iconColor ?? accentColor }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-[var(--color-text-primary)] text-sm leading-tight">{title}</h3>
        <p className="text-xs text-[var(--color-text-secondary)] mt-1 leading-relaxed">
          {description}
        </p>
      </div>
    </button>
  );
}

// ─── Call Me Section ──────────────────────────────────────────────────────────

const CALL_SESSION_OPTIONS: { type: SessionType; label: string; icon: React.ReactNode; color: string; iconColor?: string }[] = [
  { type: 'morning_checkin', label: 'Morning Check-In', icon: <Sun size={18} />, color: brandColors.teal },
  { type: 'task_planning', label: 'Task Planning', icon: <SquareCheckBig size={18} />, color: brandColors.darkBlue, iconColor: '#ffffff' },
  { type: 'coaching', label: 'Coaching Call', icon: <PhoneCall size={18} />, color: '#8B5CF6' },
  { type: 'evening_checkin', label: 'Evening Check-In', icon: <Moon size={18} />, color: brandColors.lightBlue },
];

function CallMeSection({
  canPlaceCalls,
  setupMessage,
  setupCtaLabel,
  onSelectSession,
  onOpenPhoneSetup,
}: {
  canPlaceCalls: boolean;
  setupMessage: string | null;
  setupCtaLabel: string | null;
  onSelectSession: (type: SessionType) => void;
  onOpenPhoneSetup: () => void;
}) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!canPlaceCalls) {
    return (
      <div
        className="w-full rounded-2xl px-5 py-4 flex items-center gap-3"
        style={{
          background: `linear-gradient(135deg, ${brandColors.teal}18, ${brandColors.lightBlue}18)`,
          border: `1px solid ${brandColors.teal}33`,
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${brandColors.teal}22`, color: brandColors.teal }}
        >
          <Phone size={20} />
        </div>
        <div className="text-left flex-1 min-w-0">
          <h3 className="font-semibold text-[var(--color-text-primary)] text-sm">Call me instead</h3>
          <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
            {setupMessage ?? 'Set up phone calls in Settings to start sessions by phone.'}
          </p>
        </div>
        <button
          onClick={onOpenPhoneSetup}
          className="flank-btn flank-btn-primary text-sm whitespace-nowrap"
        >
          {setupCtaLabel ?? 'Set up here'}
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded-2xl px-5 py-4 flex items-center gap-3 transition-all hover:scale-[1.01]"
        style={{
          background: `linear-gradient(135deg, ${brandColors.teal}18, ${brandColors.lightBlue}18)`,
          border: `1px solid ${brandColors.teal}33`,
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${brandColors.teal}22`, color: brandColors.teal }}
        >
          <Phone size={20} />
        </div>
        <div className="text-left flex-1 min-w-0">
          <h3 className="font-semibold text-[var(--color-text-primary)] text-sm">Call me instead</h3>
          <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
            Have Flank call your phone to start a session
          </p>
        </div>
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div
            ref={sheetRef}
            className="w-full max-w-sm rounded-2xl p-5 flex flex-col gap-4 flank-appear"
            style={{ background: 'var(--color-background)' }}
          >
            <div>
              <h3 className="font-semibold text-[var(--color-text-primary)]">What type of session?</h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                Pick a session and we&apos;ll call your phone.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {CALL_SESSION_OPTIONS.map(({ type, label, icon, color, iconColor }) => (
                <button
                  key={type}
                  onClick={() => { setOpen(false); onSelectSession(type); }}
                  className="flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-[var(--color-surface)]"
                  style={{ border: '1px solid var(--color-border)' }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${color}22`, color: iconColor ?? color }}
                  >
                    {icon}
                  </div>
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">{label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="flank-btn flank-btn-secondary w-full text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Focused Tasks Section ────────────────────────────────────────────────────

function FocusedTasks({
  dayFocuses,
  hasPrimaryDayFocus,
  onCreateDayFocus,
  onCreateQuickPlan,
  onToggleFocusItem,
  onAddFocusItem,
  onDeleteFocusItem,
  onReorderFocusItems,
  tasks,
  onToggle,
  onAddItem,
  onDeleteItem,
  onReorder,
  onViewAll,
}: {
  dayFocuses: DayFocus[];
  hasPrimaryDayFocus: boolean;
  onCreateDayFocus: (input: {
    title: string;
    summary?: string;
    priority: DayFocusPriority;
    actionItems: string[];
  }) => Promise<string | null>;
  onCreateQuickPlan: (input: {
    instruction: string;
    inputMode: 'text' | 'voice';
  }) => Promise<GenerateQuickPlanResponse>;
  onToggleFocusItem: (focusId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onAddFocusItem: (focusId: string, text: string) => Promise<unknown>;
  onDeleteFocusItem: (focusId: string, actionItemId: string) => Promise<void>;
  onReorderFocusItems: (
    focusId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>
  ) => Promise<void>;
  tasks: TaskPlan[];
  onToggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onAddItem: (taskId: string, text: string) => Promise<unknown>;
  onDeleteItem: (taskId: string, actionItemId: string) => Promise<void>;
  onReorder: (
    taskId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>
  ) => Promise<void>;
  onViewAll: () => void;
}) {
  const toDateKey = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  const todayKey = useMemo(() => toDateKey(new Date()), [toDateKey]);
  const isActionItemDone = useCallback(
    (item: TaskPlan['actionItems'][number]) => (item.status ? item.status === 'done' : item.completed),
    []
  );
  const isTaskListComplete = useCallback((task: TaskPlan) => {
    if (task.status === 'completed') return true;
    if (task.actionItems.length === 0) return false;
    return task.actionItems.every(isActionItemDone);
  }, [isActionItemDone]);
  const taskDateKey = useCallback((task: TaskPlan) => {
    if (task.createdAt?.toDate) {
      return toDateKey(task.createdAt.toDate());
    }
    const timestamp = task.createdAt as unknown as { seconds?: number };
    const fallbackDate = new Date((timestamp.seconds ?? Date.now() / 1000) * 1000);
    return toDateKey(fallbackDate);
  }, [toDateKey]);

  const TASK_COMPLETION_HOLD_MS = 700;
  const TASK_EXIT_ANIMATION_MS = 420;
  const TASK_EXIT_MAX_HEIGHT_PX = 1800;

  type CompletionPhase = 'holding' | 'pending-exit' | 'exiting';
  type CompletingTaskEntry = {
    task: TaskPlan;
    phase: CompletionPhase;
    exitIndex: number;
  };

  const [openId, setOpenId] = useState<string | null>(null);
  const [completingTasks, setCompletingTasks] = useState<Record<string, CompletingTaskEntry>>({});
  const [completingFocuses, setCompletingFocuses] = useState<Record<string, CompletingTaskEntry>>({});
  const [enteringTaskIds, setEnteringTaskIds] = useState<Record<string, true>>({});
  const [showPreviousTasks, setShowPreviousTasks] = useState(false);
  const [showAddFocusModal, setShowAddFocusModal] = useState(false);
  const [showQuickPlanModal, setShowQuickPlanModal] = useState(false);
  const [quickPlanFeedback, setQuickPlanFeedback] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchClosing, setSearchClosing] = useState(false);
  const [newFocusTitle, setNewFocusTitle] = useState('');
  const [newFocusPriority, setNewFocusPriority] = useState<DayFocusPriority>('primary');
  const [newFocusActionItemText, setNewFocusActionItemText] = useState('');
  const [newFocusActionItems, setNewFocusActionItems] = useState<string[]>([]);
  const [creatingFocus, setCreatingFocus] = useState(false);
  const [addFocusError, setAddFocusError] = useState<string | null>(null);
  const newFocusActionItemInputRef = useRef<HTMLInputElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchCloseTimerRef = useRef<number | null>(null);
  const completionTimersRef = useRef<Record<string, { hold?: number; remove?: number }>>({});
  const completionFocusTimersRef = useRef<Record<string, { hold?: number; remove?: number }>>({});
  const taskContainerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const previousTopRef = useRef<Record<string, number>>({});
  const flipTimersRef = useRef<Record<string, number>>({});
  const knownTaskIdsRef = useRef<Set<string>>(new Set(tasks.map((task) => task.id)));
  const previousLayoutTriggerRef = useRef<string>('');
  const suppressNextFlipRef = useRef(false);
  const todayActiveDayFocuses = useMemo(
    () => dayFocuses
      .filter((focus) => focus.focusDate === todayKey && focus.status !== 'completed')
      .sort((a, b) => (a.priority === 'primary' ? -1 : b.priority === 'primary' ? 1 : 0)),
    [dayFocuses, todayKey]
  );

  const dayFocusAsTaskPlan = useCallback((focus: DayFocus): TaskPlan => ({
    id: focus.id,
    userId: focus.userId,
    sessionId: focus.sessionId,
    title: focus.title,
    summary: focus.summary ?? '',
    status: focus.status,
    actionItems: focus.actionItems,
    createdAt: focus.createdAt,
    updatedAt: focus.updatedAt,
    completedAt: focus.completedAt,
  }), []);

  const previousDayFocusesAsTasks = useMemo(
    () => dayFocuses
      .filter((focus) => focus.focusDate !== todayKey)
      .map(dayFocusAsTaskPlan)
      .filter((task) => !isTaskListComplete(task)),
    [dayFocuses, todayKey, dayFocusAsTaskPlan, isTaskListComplete]
  );
  const visibleCardIds = useMemo(
    () => [
      ...todayActiveDayFocuses.map((focus) => focus.id),
      ...previousDayFocusesAsTasks.map((focus) => focus.id),
      ...tasks.map((task) => task.id),
    ],
    [todayActiveDayFocuses, previousDayFocusesAsTasks, tasks]
  );

  const addPendingFocusActionItem = useCallback(() => {
    const trimmed = newFocusActionItemText.trim();
    if (!trimmed) return;
    setNewFocusActionItems((prev) => [...prev, trimmed]);
    setNewFocusActionItemText('');
    window.requestAnimationFrame(() => {
      newFocusActionItemInputRef.current?.focus();
    });
  }, [newFocusActionItemText]);

  const removePendingFocusActionItem = useCallback((index: number) => {
    setNewFocusActionItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetAddFocusForm = useCallback(() => {
    setShowAddFocusModal(false);
    setNewFocusTitle('');
    setNewFocusPriority('primary');
    setNewFocusActionItemText('');
    setNewFocusActionItems([]);
    setCreatingFocus(false);
    setAddFocusError(null);
  }, []);

  const handleCreateQuickPlan = useCallback(async (
    input: { instruction: string; inputMode: 'text' | 'voice' }
  ) => {
    const result = await onCreateQuickPlan(input);
    setQuickPlanFeedback(`Added quick plan "${result.title}".`);
    setOpenId(result.taskId);
  }, [onCreateQuickPlan]);

  useEffect(() => {
    if (!quickPlanFeedback) return undefined;
    const timer = window.setTimeout(() => setQuickPlanFeedback(null), 4500);
    return () => window.clearTimeout(timer);
  }, [quickPlanFeedback]);

  useEffect(() => {
    return () => {
      if (searchCloseTimerRef.current) {
        window.clearTimeout(searchCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const rafId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [searchOpen]);

  const SEARCH_CLOSE_ANIMATION_MS = 300;
  const searchOverlayActive = searchOpen || searchClosing;

  const openSearch = useCallback(() => {
    if (searchCloseTimerRef.current) {
      window.clearTimeout(searchCloseTimerRef.current);
      searchCloseTimerRef.current = null;
    }
    setSearchClosing(false);
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    if (!searchOpen && !searchClosing) return;
    if (searchCloseTimerRef.current) {
      window.clearTimeout(searchCloseTimerRef.current);
    }
    setSearchOpen(false);
    setSearchClosing(true);
    searchCloseTimerRef.current = window.setTimeout(() => {
      setSearchClosing(false);
      setSearchQuery('');
      searchCloseTimerRef.current = null;
    }, SEARCH_CLOSE_ANIMATION_MS);
  }, [searchClosing, searchOpen]);

  const submitAddFocus = useCallback(async () => {
    const title = newFocusTitle.trim();
    if (!title || newFocusActionItems.length === 0 || creatingFocus) return;
    setAddFocusError(null);
    setCreatingFocus(true);
    try {
      const createdId = await onCreateDayFocus({
        title,
        priority: newFocusPriority,
        actionItems: newFocusActionItems,
      });
      if (!createdId) {
        setCreatingFocus(false);
        setAddFocusError('Could not save focus. Please try again.');
        return;
      }
      resetAddFocusForm();
    } catch (error) {
      setCreatingFocus(false);
      const message = error instanceof Error ? error.message : 'Could not save focus. Please try again.';
      setAddFocusError(message);
    }
  }, [
    creatingFocus,
    newFocusTitle,
    newFocusActionItems,
    newFocusPriority,
    onCreateDayFocus,
    resetAddFocusForm,
  ]);

  const clearCompletionTimers = useCallback((taskId: string) => {
    const timers = completionTimersRef.current[taskId];
    if (!timers) return;
    if (timers.hold) window.clearTimeout(timers.hold);
    if (timers.remove) window.clearTimeout(timers.remove);
    delete completionTimersRef.current[taskId];
  }, []);

  const dropCompletingTask = useCallback((taskId: string) => {
    clearCompletionTimers(taskId);
    setCompletingTasks((prev) => {
      if (!(taskId in prev)) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, [clearCompletionTimers]);

  const clearFocusCompletionTimers = useCallback((focusId: string) => {
    const timers = completionFocusTimersRef.current[focusId];
    if (!timers) return;
    if (timers.hold) window.clearTimeout(timers.hold);
    if (timers.remove) window.clearTimeout(timers.remove);
    delete completionFocusTimersRef.current[focusId];
  }, []);

  const dropCompletingFocus = useCallback((focusId: string) => {
    clearFocusCompletionTimers(focusId);
    setCompletingFocuses((prev) => {
      if (!(focusId in prev)) return prev;
      const next = { ...prev };
      delete next[focusId];
      return next;
    });
  }, [clearFocusCompletionTimers]);

  useEffect(() => {
    return () => {
      Object.keys(completionTimersRef.current).forEach((taskId) => {
        const timers = completionTimersRef.current[taskId];
        if (timers.hold) window.clearTimeout(timers.hold);
        if (timers.remove) window.clearTimeout(timers.remove);
      });
      Object.keys(completionFocusTimersRef.current).forEach((focusId) => {
        const timers = completionFocusTimersRef.current[focusId];
        if (timers.hold) window.clearTimeout(timers.hold);
        if (timers.remove) window.clearTimeout(timers.remove);
      });
      Object.values(flipTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      completionTimersRef.current = {};
      completionFocusTimersRef.current = {};
      flipTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    const entering: Record<string, true> = {};
    tasks.forEach((task) => {
      if (!knownTaskIdsRef.current.has(task.id)) entering[task.id] = true;
    });
    setEnteringTaskIds(entering);
    tasks.forEach((task) => knownTaskIdsRef.current.add(task.id));
  }, [tasks]);

  const startCompletionAnimation = useCallback((task: TaskPlan, actionItemId: string, exitIndex: number) => {
    if (completionTimersRef.current[task.id]) return;

    const completedTask: TaskPlan = {
      ...task,
      status: 'completed',
      actionItems: task.actionItems.map((item) => (
        item.id === actionItemId ? { ...item, completed: true } : item
      )),
    };

    setCompletingTasks((prev) => ({
      ...prev,
      [task.id]: { task: completedTask, phase: 'holding', exitIndex },
    }));

    const holdTimer = window.setTimeout(() => {
      setCompletingTasks((prev) => {
        const entry = prev[task.id];
        if (!entry) return prev;
        return {
          ...prev,
          [task.id]: { ...entry, phase: 'pending-exit' },
        };
      });
    }, TASK_COMPLETION_HOLD_MS);

    completionTimersRef.current[task.id] = { hold: holdTimer };
  }, []);

  const startFocusCompletionAnimation = useCallback((focus: TaskPlan, actionItemId: string, exitIndex: number) => {
    if (completionFocusTimersRef.current[focus.id]) return;

    const completedFocus: TaskPlan = {
      ...focus,
      status: 'completed',
      actionItems: focus.actionItems.map((item) => (
        item.id === actionItemId ? { ...item, completed: true } : item
      )),
    };

    setCompletingFocuses((prev) => ({
      ...prev,
      [focus.id]: { task: completedFocus, phase: 'holding', exitIndex },
    }));

    const holdTimer = window.setTimeout(() => {
      setCompletingFocuses((prev) => {
        const entry = prev[focus.id];
        if (!entry) return prev;
        return {
          ...prev,
          [focus.id]: { ...entry, phase: 'pending-exit' },
        };
      });
    }, TASK_COMPLETION_HOLD_MS);

    completionFocusTimersRef.current[focus.id] = { hold: holdTimer };
  }, []);

  const handleToggle = useCallback(async (
    taskId: string,
    actionItemId: string,
    completed: boolean
  ) => {
    const taskIndex = tasks.findIndex((candidate) => candidate.id === taskId);
    const task = tasks.find((candidate) => candidate.id === taskId);
    const isFinishingLastItem = task
      ? completed &&
        task.actionItems.some((item) => item.id === actionItemId && !item.completed) &&
        task.actionItems.filter((item) => !item.completed).length === 1
      : false;

    if (isFinishingLastItem && task && taskIndex >= 0) {
      startCompletionAnimation(task, actionItemId, taskIndex);
    }

    try {
      await onToggle(taskId, actionItemId, completed);
    } catch (error) {
      dropCompletingTask(taskId);
      throw error;
    }
  }, [tasks, onToggle, startCompletionAnimation, dropCompletingTask]);

  const handleToggleFocusItem = useCallback(async (
    focusId: string,
    actionItemId: string,
    completed: boolean
  ) => {
    const focusIndex = todayActiveDayFocuses.findIndex((f) => f.id === focusId);
    const focus = todayActiveDayFocuses.find((f) => f.id === focusId);
    const focusAsTask = focus ? dayFocusAsTaskPlan(focus) : null;
    const isFinishingLastItem = focusAsTask
      ? completed &&
        focusAsTask.actionItems.some((item) => item.id === actionItemId && !item.completed) &&
        focusAsTask.actionItems.filter((item) => !item.completed).length === 1
      : false;

    if (isFinishingLastItem && focusAsTask && focusIndex >= 0) {
      startFocusCompletionAnimation(focusAsTask, actionItemId, focusIndex);
    }

    try {
      await onToggleFocusItem(focusId, actionItemId, completed);
    } catch (error) {
      dropCompletingFocus(focusId);
      throw error;
    }
  }, [todayActiveDayFocuses, dayFocusAsTaskPlan, onToggleFocusItem, startFocusCompletionAnimation, dropCompletingFocus]);

  const activeTaskIds = useMemo(
    () => new Set(tasks.map((task) => task.id)),
    [tasks]
  );
  const activeTaskOrderKey = useMemo(
    () => tasks.map((task) => task.id).join('|'),
    [tasks]
  );
  const exitingTaskIdsKey = useMemo(
    () => Object.entries(completingTasks)
      .filter(([, entry]) => entry.phase === 'exiting')
      .map(([taskId]) => taskId)
      .sort()
      .join('|'),
    [completingTasks]
  );
  const layoutTriggerKey = `${activeTaskOrderKey}__${exitingTaskIdsKey}`;

  useEffect(() => {
    Object.entries(completingTasks).forEach(([taskId, entry]) => {
      if (entry.phase !== 'pending-exit') return;
      if (activeTaskIds.has(taskId)) return;
      if (completionTimersRef.current[taskId]?.remove) return;

      setCompletingTasks((prev) => {
        const current = prev[taskId];
        if (!current || current.phase !== 'pending-exit') return prev;
        return {
          ...prev,
          [taskId]: { ...current, phase: 'exiting' },
        };
      });

      const removeTimer = window.setTimeout(() => {
        // The card has already collapsed out visually; skipping the next FLIP avoids
        // a second tiny "settle" animation when the ghost node is removed.
        suppressNextFlipRef.current = true;
        dropCompletingTask(taskId);
      }, TASK_EXIT_ANIMATION_MS);

      completionTimersRef.current[taskId] = {
        ...completionTimersRef.current[taskId],
        remove: removeTimer,
      };
    });
  }, [activeTaskIds, completingTasks, dropCompletingTask]);

  const activeFocusIds = useMemo(
    () => new Set(todayActiveDayFocuses.map((focus) => focus.id)),
    [todayActiveDayFocuses]
  );

  useEffect(() => {
    Object.entries(completingFocuses).forEach(([focusId, entry]) => {
      if (entry.phase !== 'pending-exit') return;
      if (activeFocusIds.has(focusId)) return;
      if (completionFocusTimersRef.current[focusId]?.remove) return;

      setCompletingFocuses((prev) => {
        const current = prev[focusId];
        if (!current || current.phase !== 'pending-exit') return prev;
        return {
          ...prev,
          [focusId]: { ...current, phase: 'exiting' },
        };
      });

      const removeTimer = window.setTimeout(() => {
        dropCompletingFocus(focusId);
      }, TASK_EXIT_ANIMATION_MS);

      completionFocusTimersRef.current[focusId] = {
        ...completionFocusTimersRef.current[focusId],
        remove: removeTimer,
      };
    });
  }, [activeFocusIds, completingFocuses, dropCompletingFocus]);

  const renderedTasks = useMemo(() => {
    const activeOrReplacing = tasks.map((task) => {
      const completingEntry = completingTasks[task.id];
      return completingEntry ? completingEntry.task : task;
    });

    const exitingGhostEntries = Object.entries(completingTasks)
      .filter(([taskId]) => !activeTaskIds.has(taskId))
      .map(([, entry]) => entry)
      .sort((a, b) => a.exitIndex - b.exitIndex);

    const withExitingAtOriginalPosition = [...activeOrReplacing];
    let insertedCount = 0;
    exitingGhostEntries.forEach((entry) => {
      const insertAt = Math.min(
        Math.max(entry.exitIndex + insertedCount, 0),
        withExitingAtOriginalPosition.length
      );
      withExitingAtOriginalPosition.splice(insertAt, 0, entry.task);
      insertedCount += 1;
    });

    return withExitingAtOriginalPosition;
  }, [tasks, completingTasks, activeTaskIds]);

  const renderedFocuses = useMemo(() => {
    const activeOrReplacing = todayActiveDayFocuses.map((focus) => {
      const completingEntry = completingFocuses[focus.id];
      return completingEntry ? completingEntry.task : dayFocusAsTaskPlan(focus);
    });

    const exitingGhostEntries = Object.entries(completingFocuses)
      .filter(([focusId]) => !activeFocusIds.has(focusId))
      .map(([, entry]) => entry)
      .sort((a, b) => a.exitIndex - b.exitIndex);

    const withExitingAtOriginalPosition = [...activeOrReplacing];
    let insertedCount = 0;
    exitingGhostEntries.forEach((entry) => {
      const insertAt = Math.min(
        Math.max(entry.exitIndex + insertedCount, 0),
        withExitingAtOriginalPosition.length
      );
      withExitingAtOriginalPosition.splice(insertAt, 0, entry.task);
      insertedCount += 1;
    });

    return withExitingAtOriginalPosition;
  }, [todayActiveDayFocuses, completingFocuses, activeFocusIds, dayFocusAsTaskPlan]);

  const todayRenderedTasks = useMemo(
    () => renderedTasks.filter((task) => taskDateKey(task) === todayKey),
    [renderedTasks, taskDateKey, todayKey]
  );
  const previousRenderedTasks = useMemo(
    () => renderedTasks.filter((task) => taskDateKey(task) !== todayKey),
    [renderedTasks, taskDateKey, todayKey]
  );
  const previousItemCount = previousDayFocusesAsTasks.length + previousRenderedTasks.length;
  const searchFilter = useCallback((task: TaskPlan) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    if (task.title.toLowerCase().includes(q)) return true;
    if (task.summary?.toLowerCase().includes(q)) return true;
    if (task.actionItems.some((ai) => ai.text.toLowerCase().includes(q))) return true;
    return false;
  }, [searchQuery]);

  const filteredFocuses = useMemo(
    () => renderedFocuses.filter(searchFilter),
    [renderedFocuses, searchFilter]
  );
  const filteredTodayTasks = useMemo(
    () => todayRenderedTasks.filter(searchFilter),
    [todayRenderedTasks, searchFilter]
  );

  const hasTodayItems = renderedFocuses.length > 0 || todayRenderedTasks.length > 0;
  const hasAnyItems = hasTodayItems || previousItemCount > 0;

  const renderTaskCard = useCallback((task: TaskPlan) => {
    const completionState = completingTasks[task.id];
    const isCompleting = Boolean(completionState);
    const isExiting = completionState?.phase === 'exiting';
    const isEntering = Boolean(enteringTaskIds[task.id]);

    return (
      <div
        key={task.id}
        style={{
          overflow: 'hidden',
          maxHeight: isExiting ? 0 : TASK_EXIT_MAX_HEIGHT_PX,
          opacity: isExiting ? 0 : 1,
          transform: isExiting ? 'translateX(-36px)' : 'translateX(0)',
          marginBottom: isExiting ? 0 : 12,
          pointerEvents: isCompleting ? 'none' : 'auto',
          transition: [
            `max-height ${TASK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
            `opacity ${TASK_EXIT_ANIMATION_MS - 80}ms ease`,
            `transform ${TASK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
            `margin-bottom ${TASK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
          ].join(', '),
        }}
      >
        <div
          ref={(node) => {
            taskContainerRefs.current[task.id] = node;
          }}
        >
          <CollapsibleTaskCard
            task={task}
            open={openId === task.id}
            onOpenToggle={() => setOpenId((prev) => (prev === task.id ? null : task.id))}
            onToggle={handleToggle}
            onAddItem={onAddItem}
            onDeleteItem={onDeleteItem}
            onReorder={onReorder}
            animationDelay={isEntering ? 40 : 0}
            isCompleting={isCompleting}
          />
        </div>
      </div>
    );
  }, [
    TASK_EXIT_ANIMATION_MS,
    TASK_EXIT_MAX_HEIGHT_PX,
    completingTasks,
    enteringTaskIds,
    handleToggle,
    onAddItem,
    onDeleteItem,
    onReorder,
    openId,
  ]);

  const renderFocusCard = useCallback((task: TaskPlan) => {
    const completionState = completingFocuses[task.id];
    const isCompleting = Boolean(completionState);
    const isExiting = completionState?.phase === 'exiting';

    return (
      <div
        key={task.id}
        style={{
          overflow: 'hidden',
          maxHeight: isExiting ? 0 : TASK_EXIT_MAX_HEIGHT_PX,
          opacity: isExiting ? 0 : 1,
          transform: isExiting ? 'translateX(-36px)' : 'translateX(0)',
          marginBottom: isExiting ? 0 : 12,
          pointerEvents: isCompleting ? 'none' : 'auto',
          transition: [
            `max-height ${TASK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
            `opacity ${TASK_EXIT_ANIMATION_MS - 80}ms ease`,
            `transform ${TASK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
            `margin-bottom ${TASK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
          ].join(', '),
        }}
      >
        <CollapsibleTaskCard
          task={task}
          open={openId === task.id}
          onOpenToggle={() => setOpenId((prev) => (prev === task.id ? null : task.id))}
          onToggle={handleToggleFocusItem}
          onAddItem={onAddFocusItem}
          onDeleteItem={onDeleteFocusItem}
          onReorder={onReorderFocusItems}
          isCompleting={isCompleting}
          variant="dayFocus"
        />
      </div>
    );
  }, [
    TASK_EXIT_ANIMATION_MS,
    TASK_EXIT_MAX_HEIGHT_PX,
    completingFocuses,
    handleToggleFocusItem,
    onAddFocusItem,
    onDeleteFocusItem,
    onReorderFocusItems,
    openId,
  ]);

  useLayoutEffect(() => {
    const nextTop: Record<string, number> = {};
    const shouldAnimate =
      previousLayoutTriggerRef.current !== layoutTriggerKey &&
      !suppressNextFlipRef.current;

    renderedTasks.forEach((task) => {
      const el = taskContainerRefs.current[task.id];
      if (!el) return;
      const currentTop = el.getBoundingClientRect().top;
      nextTop[task.id] = currentTop;
      if (!shouldAnimate) return;

      const completionState = completingTasks[task.id];
      if (completionState?.phase === 'exiting') return;

      const previousTop = previousTopRef.current[task.id];
      if (previousTop === undefined) return;

      const deltaY = previousTop - currentTop;
      if (Math.abs(deltaY) < 6) return;

      el.style.transition = 'none';
      el.style.transform = `translateY(${deltaY}px)`;
      void el.getBoundingClientRect();
      el.style.transition = 'transform 560ms cubic-bezier(0.22, 1, 0.3, 1.3)';
      el.style.transform = 'translateY(0)';

      const existingTimer = flipTimersRef.current[task.id];
      if (existingTimer) window.clearTimeout(existingTimer);
      flipTimersRef.current[task.id] = window.setTimeout(() => {
        const node = taskContainerRefs.current[task.id];
        if (!node) return;
        node.style.transition = '';
        node.style.transform = '';
        delete flipTimersRef.current[task.id];
      }, 620);
    });

    previousTopRef.current = nextTop;
    previousLayoutTriggerRef.current = layoutTriggerKey;
    suppressNextFlipRef.current = false;
  }, [renderedTasks, completingTasks, layoutTriggerKey]);

  useEffect(() => {
    if (visibleCardIds.length === 0) {
      if (openId !== null) setOpenId(null);
      return;
    }
    if (openId && visibleCardIds.includes(openId)) return;
    if (visibleCardIds.length === 1) {
      setOpenId(visibleCardIds[0]);
      return;
    }
    setOpenId(null);
  }, [visibleCardIds, openId]);

  return (
    <section className="flank-appear">
      <div className="relative flex items-center justify-between mb-3 gap-2 min-h-[30px]">
        <h2
          className={`text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider shrink-0 transition-opacity ${
            searchOverlayActive ? 'opacity-0 lg:opacity-100' : 'opacity-100'
          }`}
        >
          Today&apos;s focus
        </h2>
        <div
          className={`flex items-center gap-2 flex-1 justify-end ${
            searchOverlayActive ? 'absolute inset-0 z-10 lg:static lg:inset-auto' : ''
          }`}
        >
          <div
            className={`flex items-center justify-end transition-all duration-300 ease-out ${
              searchOverlayActive ? 'flex-1' : 'flex-none'
            }`}
          >
            <button
              onClick={openSearch}
              className={`inline-flex items-center justify-center rounded-full text-[var(--color-text-muted)] transition-[width,opacity,transform,background-color] duration-300 h-8 ${
                searchOverlayActive
                  ? 'w-0 opacity-0 scale-90 pointer-events-none'
                  : 'w-8 opacity-100 scale-100 hover:bg-[var(--color-surface)]'
              }`}
              title="Search tasks"
              aria-label="Search tasks"
            >
              <Search size={14} />
            </button>
            <div
              className={`flex items-center gap-1.5 rounded-full h-8 min-w-0 overflow-hidden transition-[max-width,opacity,transform,padding,border-color] duration-300 ease-out ${
                searchOverlayActive
                  ? 'flex-1 lg:max-w-[220px] opacity-100 scale-100 px-2.5 border'
                  : 'max-w-0 opacity-0 scale-95 px-0 border border-transparent'
              }`}
              style={{
                background: 'var(--color-surface)',
                borderColor: searchOverlayActive ? 'var(--color-border)' : 'transparent',
                maxWidth: searchOpen ? '24rem' : '0px',
              }}
            >
              <Search size={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search…"
                className="flex-1 bg-transparent text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none min-w-0"
              />
              <button
                onClick={closeSearch}
                className="shrink-0 hover:opacity-70 transition-opacity"
                aria-label="Close search"
              >
                <X size={12} style={{ color: 'var(--color-text-muted)' }} />
              </button>
            </div>
          </div>
          <button
            onClick={() => setShowQuickPlanModal(true)}
            className="lg:hidden inline-flex items-center justify-center h-8 w-8 rounded-full transition-colors hover:bg-[var(--color-surface)]"
            title="Quick Plan"
            aria-label="Quick Plan"
            style={{ color: brandColors.teal }}
          >
            <QuickTaskIcon size={18} />
          </button>
          <button
            onClick={() => setShowQuickPlanModal(true)}
            className="hidden lg:inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-semibold border transition-[transform,filter] hover:brightness-95 active:scale-[0.98]"
            title="Quick Plan"
            aria-label="Quick Plan"
            style={{
              color: brandColors.teal,
              borderColor: `${brandColors.teal}99`,
              background: `${brandColors.teal}1A`,
            }}
          >
            <QuickTaskIcon size={18} />
            Quick Plan
          </button>
          {!hasPrimaryDayFocus && (
            <button
              onClick={() => setShowAddFocusModal(true)}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-[transform,filter] hover:brightness-95 active:scale-[0.98]"
              style={{
                color: brandColors.lightBlue,
                borderColor: `${brandColors.lightBlue}99`,
                background: `${brandColors.lightBlue}1A`,
              }}
            >
              <Eye size={13} />
              Add Day Focus
            </button>
          )}
          <button
            onClick={onViewAll}
            className="text-xs font-medium shrink-0"
            style={{ color: 'var(--color-brand-primary)' }}
          >
            View all
          </button>
        </div>
      </div>
      {quickPlanFeedback && (
        <div
          className="rounded-xl px-3 py-2 text-xs mb-3"
          style={{ background: 'var(--color-success-subtle)', color: 'var(--color-success)' }}
        >
          {quickPlanFeedback}
        </div>
      )}

      <div className="flex flex-col">
        {filteredFocuses.map((focus) => renderFocusCard(focus))}

        {filteredTodayTasks.map((task) => renderTaskCard(task))}

        {!hasTodayItems && (
          <div
            className="rounded-xl px-4 py-4 text-sm mb-3"
            style={{ background: 'var(--color-surface)', color: 'var(--color-text-muted)' }}
          >
            No tasks in today&apos;s focus yet.
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowPreviousTasks((prev) => !prev)}
          disabled={previousItemCount === 0}
          className="w-full rounded-xl px-4 py-3 flex items-center justify-between text-left transition-colors disabled:opacity-60"
          style={{
            background: 'transparent',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-secondary)',
            marginBottom: showPreviousTasks ? 10 : 0,
            cursor: previousItemCount === 0 ? 'default' : 'pointer',
          }}
        >
          <span className="text-xs font-semibold uppercase tracking-wider">
            Previous tasks ({previousItemCount})
          </span>
          <ChevronDown
            size={16}
            className="transition-transform duration-200"
            style={{ transform: showPreviousTasks ? 'rotate(180deg)' : undefined }}
          />
        </button>

        <div
          className="grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{
            gridTemplateRows: showPreviousTasks ? '1fr' : '0fr',
            opacity: showPreviousTasks ? 1 : 0.9,
          }}
        >
          <div className="overflow-hidden">
            {showPreviousTasks && (
              <div className="pt-1">
                {previousDayFocusesAsTasks.map((task) => {
                  return (
                    <div key={task.id} style={{ marginBottom: 12 }}>
                      <CollapsibleTaskCard
                        task={task}
                        open={openId === task.id}
                        onOpenToggle={() => setOpenId((prev) => (prev === task.id ? null : task.id))}
                        onToggle={onToggleFocusItem}
                        onAddItem={onAddFocusItem}
                        onDeleteItem={onDeleteFocusItem}
                        onReorder={onReorderFocusItems}
                      />
                    </div>
                  );
                })}

                {previousRenderedTasks.map((task) => renderTaskCard(task))}
              </div>
            )}
          </div>
        </div>

        {!hasAnyItems && (
          <div
            className="rounded-xl px-4 py-4 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            style={{ background: 'var(--color-surface)', color: 'var(--color-text-muted)' }}
          >
            <span>Add a day focus to anchor what matters most today.</span>
            <button
              type="button"
              onClick={() => setShowAddFocusModal(true)}
              className="inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold border transition-[transform,filter] hover:brightness-95 active:scale-[0.98] self-start sm:self-auto whitespace-nowrap"
              style={{
                color: brandColors.teal,
                borderColor: `${brandColors.teal}99`,
                background: `${brandColors.teal}1A`,
              }}
            >
              Set focus
            </button>
          </div>
        )}
      </div>

      {showAddFocusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[1px] flex items-end sm:items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-lg rounded-2xl p-6 flex flex-col gap-4"
            style={{
              background: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <div>
              <h3 className="font-semibold text-[var(--color-text-primary)]">Add Day Focus</h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                Capture a clear focus and a few concrete steps for today.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <input
                value={newFocusTitle}
                onChange={(e) => setNewFocusTitle(e.target.value)}
                placeholder="Focus title"
                className="w-full rounded-lg border px-3 py-2 text-sm bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                style={{ borderColor: 'var(--color-border)' }}
              />

              <select
                value={newFocusPriority}
                onChange={(e) => setNewFocusPriority(e.target.value as DayFocusPriority)}
                className="w-full rounded-lg border px-3 py-2 text-sm bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <option value="primary">Primary focus</option>
                <option value="secondary">Secondary focus</option>
              </select>

              <div className="flex gap-2">
                <input
                  ref={newFocusActionItemInputRef}
                  value={newFocusActionItemText}
                  onChange={(e) => setNewFocusActionItemText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addPendingFocusActionItem();
                    }
                  }}
                  placeholder="Add action item"
                  className="flex-1 rounded-lg border px-3 py-2 text-sm bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                  style={{ borderColor: 'var(--color-border)' }}
                />
                <button
                  onClick={addPendingFocusActionItem}
                  className="flank-btn flank-btn-secondary text-sm"
                  disabled={!newFocusActionItemText.trim()}
                >
                  Add
                </button>
              </div>

              {newFocusActionItems.length > 0 && (
                <div className="rounded-lg p-2" style={{ background: 'var(--color-surface)' }}>
                  {newFocusActionItems.map((item, idx) => (
                    <div key={`${item}-${idx}`} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-[var(--color-text-primary)]">{item}</span>
                      <button
                        onClick={() => removePendingFocusActionItem(idx)}
                        className="text-xs"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {addFocusError && (
                <div
                  className="rounded-lg px-3 py-2 text-sm"
                  style={{ background: 'var(--color-error-subtle)', color: 'var(--color-error)' }}
                >
                  {addFocusError}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={resetAddFocusForm}
                className="flank-btn flank-btn-secondary flex-1"
                disabled={creatingFocus}
              >
                Cancel
              </button>
              <button
                onClick={submitAddFocus}
                className="flank-btn flex-1 hover:opacity-90"
                style={{
                  background: 'var(--color-brand-primary)',
                  color: 'white',
                  border: '1.5px solid var(--color-brand-primary)',
                }}
                disabled={!newFocusTitle.trim() || newFocusActionItems.length === 0 || creatingFocus}
              >
                {creatingFocus ? 'Saving...' : 'Save Focus'}
              </button>
            </div>
          </div>
        </div>
      )}

      <QuickPlanModal
        open={showQuickPlanModal}
        onClose={() => setShowQuickPlanModal(false)}
        onCreate={handleCreateQuickPlan}
      />
    </section>
  );
}

